import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Alert,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Divider,
  LinearProgress,
  TableFooter,
} from '@mui/material';
import {
  DatePicker,
  LocalizationProvider
} from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { Refresh, Download, ExpandMore, ExpandLess, Replay } from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../../../../hooks/store-hooks';
import { useRelatoriosDashService } from '../../../../../../use-cases/relatorios-dash';
import {
  setMeusRelatorios,
  setCarregandoRelatorios,
  setRelatorioExpandido,
  setPageMeusRelatorios,
  setRowsPerPageMeusRelatorios,
  setRelatorioSemArquivo,
  addReprocessandoId,
  removeReprocessandoId
} from '../../../../../../application/states/relatorios-dash';
import { useClientesService } from '../../../../../../use-cases/clientes';
import { useProdutoService } from '../../../../../../use-cases/produto';
import { useUsuariosService } from '../../../../../../use-cases/usuarios';
import {
  IRelatorioAcessRequest,
  IRelatorioAcessSource,
  IRelatorioAcessRow,
  FileType,
} from '../../../../../../application/models/IRelatorioDash';
import { IClienteTable, ITipoUsuario } from '../../../../../../application/models';
import { formatarNumero } from '../../../../../../utils/formatters';
import { useFilaStatus } from '../../../../../hooks/useFilaStatus';
import { getMensagemDisponibilidadeRelatorios } from '../../../../../../utils';
import { useTipoConsumoService } from '../../../../../../use-cases/tipo-consumo';

const RelatoriosAcessos: React.FC = () => {

  // Serviços
  const {
    getRelatorioAcess,
    getMeusRelatorios,
    reprocessarRelatorioComPolling,
    downloadArquivoRelatorio,
    temRelatoriosProcessando,
    verificarMudancasRelatorios,
    deveAtualizarListaRelatorios,
    filaIdAindaProcessando,
    filaIdProcessadoComArquivo
  } = useRelatoriosDashService();

  // 🔍 LOG DE RE-RENDER
  const { setClientesPagination, buscarClientes: buscarClientesService } = useClientesService();
  const { obterProdutos } = useProdutoService();
  const { setAllProdutosAndTipoConsumo } = useTipoConsumoService();
  const { setUsuarios, setClienteSelecionado: setClienteSelecionadoRedux, setResetClienteSelecionado } = useUsuariosService();

  // Estados do Redux
  const {
    acess: relatorioAcess,
    meusRelatorios,
    carregandoRelatorios,
    relatorioExpandido,
    pageMeusRelatorios,
    rowsPerPageMeusRelatorios,
    reprocessandoIds
  } = useAppSelector((state: any) => state.relatoriosDash);
  const { usuarios, clienteSelecionado: clienteRedux } = useAppSelector((state: any) => state.usuarios);
  const { currentUser } = useAppSelector((state: any) => state.user);

  const dispatch = useAppDispatch(); // Se precisar disparar ações diretamente

  // Useful variables - Identificação de tipo de usuário
  const isDemais = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_DEMAIS;
  const isTecnologiaDaInformacao = currentUser?.IdVelesPerfilInterno === ITipoUsuario.TECNOLOGIA_DA_INFORMACAO;
  const isComercial = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_COMERCIAL;
  const isSupervisorComercial = currentUser?.IdVelesPerfilInterno === ITipoUsuario.VELES_SUPERVISOR_COMERCIAL;
  const isCX = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_VELES_EXPERIENCIA_DO_CLIENTE;
  const isCliente = currentUser?.IdVelesPerfilInterno === ITipoUsuario.USUARIO_CLIENTE;


  // Estados locais para seletores
  const [clienteSelecionado, setClienteSelecionado] = useState<IClienteTable | null>(null);
  const [produtoSelecionado, setProdutoSelecionado] = useState<any | null>(null);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<any | null>(null);

  const [formData, setFormData] = useState<IRelatorioAcessRequest>({
    IDCLIENTE: 0,
    IDUSUARIO: undefined,
    IDUSUARIO_VENDEDOR: undefined,
    TIPOCONSUMO: undefined,
    DATAINICIO: undefined,
    DATAFIM: undefined,
    generateFiles: false,
    format: FileType.XLSX,
    customTitle: '',
    customSubtitle: '',
    includeTotal: false,
    totalLabel: '',
    sheetName: ''
  });

  const [clientesFiltrados, setClientesFiltrados] = useState<IClienteTable[]>([]);
  const [filtroBusca, setFiltroBusca] = useState('');
  const [carregandoClientes, setCarregandoClientes] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [filaId, setFilaId] = useState<number | null>(null);
  const [dataInicioError, setDataInicioError] = useState<string | null>(null);
  const [dataFimError, setDataFimError] = useState<string | null>(null);
  const [dados, setDados] = useState<IRelatorioAcessRow[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Colunas permitidas para exibição no Relatório de Acesso
  const COLUNAS_RELATORIO_ACESSO = ['Cliente', 'Produto', 'Usuário', 'Quantidade'];

  const mapearRowParaColunasFiltradas = (row: IRelatorioAcessRow): Record<string, any> => ({
    'Cliente': row.LoginCliente || row.RazaoSocial || row.NomeFantasia || '-',
    'Produto': row.Nome || row.Descricao || '-',
    'Usuário': row.LoginUsuario || '-',
    'Quantidade': formatarNumero(row.Quantidade)
  });

  const dadosExibicao = dados.map(row => mapearRowParaColunasFiltradas(row));

  const { status: filaStatus } = useFilaStatus(filaId);
  const lastFetchedClienteIdRef = useRef<number | null>(null);
  const lastSearchReturnedEmptyRef = useRef<boolean>(false);
  const pollingCyclesRef = useRef(0);
  const reprocessandoIdRef = useRef<number | null>(null);

  useEffect(() => {
    const relatoriosAcesso = meusRelatorios.filter((r: any) => r.IdRelatorioTipo === 1);
    const mine = reprocessandoIds.find((id: number) => relatoriosAcesso.some((r: any) => r.IdRelatorioFila === id)) ?? null;
    reprocessandoIdRef.current = mine;
  }, [reprocessandoIds, meusRelatorios]);

  // Só exibe o card quando há algo na fila (filaId), resultado processado (PROCESSADO) ou quando há dados retornados
  const relatorioAcessTyped = relatorioAcess as IRelatorioAcessSource | null;
  const hasDataFromBackend = relatorioAcessTyped?.data && Array.isArray(relatorioAcessTyped.data) && relatorioAcessTyped.data.length > 0;
  const hasActiveReport = filaId !== null
    || (relatorioAcessTyped?.status === 'PROCESSADO' || relatorioAcessTyped?.status === 3 || relatorioAcessTyped?.statusText === 'PROCESSADO')
    || hasDataFromBackend;
  const reportSource: IRelatorioAcessSource | null = hasActiveReport
    ? (filaId !== null ? (filaStatus ?? relatorioAcessTyped) : relatorioAcessTyped)
    : null;
  const isEmAndamento = reportSource && (reportSource.statusText === 'EM_ANDAMENTO' || reportSource.status === 2 || reportSource.status === 'EM_ANDAMENTO');
  const isProcessado = reportSource && (
    reportSource.statusText === 'PROCESSADO' || reportSource.status === 3 || reportSource.status === 'PROCESSADO'
    || (Array.isArray(reportSource.data) && reportSource.data.length > 0 && filaId === null)
  );
  const hasFilesForDownload = (filaStatus?.files?.length ?? reportSource?.files?.length ?? 0) > 0;

  const [animatedProgress, setAnimatedProgress] = useState(0);
  const progressRef = useRef(0);

  useEffect(() => {
    if (!hasActiveReport) {
      progressRef.current = 0;
      setAnimatedProgress(0);
      return;
    }

    if (isProcessado && (hasFilesForDownload || !formData.generateFiles)) {
      progressRef.current = 100;
      setAnimatedProgress(100);
      return;
    }

    if (isProcessado && !hasFilesForDownload && formData.generateFiles) {
      if (progressRef.current < 90) {
        progressRef.current = 90;
        setAnimatedProgress(90);
      }
    }

    if (!isEmAndamento && !isProcessado) return;

    const interval = setInterval(() => {
      const maxProgress = isProcessado ? 95 : 85;
      if (progressRef.current < maxProgress) {
        const increment = progressRef.current < 50 ? 3 : progressRef.current < 70 ? 2 : 1;
        progressRef.current = Math.min(progressRef.current + increment, maxProgress);
        setAnimatedProgress(progressRef.current);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [hasActiveReport, isEmAndamento, isProcessado, hasFilesForDownload, formData.generateFiles]);
  useEffect(() => {
    if (!reportSource || !isProcessado) return;
    const sourceData = reportSource.data;
    if (sourceData && Array.isArray(sourceData) && sourceData.length > 0) {
      setDados(sourceData);
    }
  }, [reportSource, isProcessado]);

  useEffect(() => {
    if (currentUser) {
      setAllProdutosAndTipoConsumo();

      setClientesPagination({
        limit: 50,
        page: 1
      });

      if (isCliente) {
        buscarClientes('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, setAllProdutosAndTipoConsumo, isCliente, setClientesPagination]);

  useEffect(() => {
    if (isCliente && !clienteSelecionado) {
      if (clienteRedux) {
        setClienteSelecionado(clienteRedux as any);
        setClientesFiltrados([clienteRedux as any]);
      } else {
        buscarClientes('');
      }
    } else if (isCliente && clientesFiltrados.length > 0) {
      const clienteDoUsuario = clientesFiltrados.find((c: any) => Number(c.IdCliente) === Number(currentUser?.IdCliente)) || clientesFiltrados[0];
      if (clienteDoUsuario && (!clienteSelecionado || clienteSelecionado.IdCliente !== clienteDoUsuario.IdCliente)) {
        setClienteSelecionado(clienteDoUsuario);
      }
    }
  }, [isCliente, clientesFiltrados, clienteSelecionado, clienteRedux, currentUser?.IdCliente]);

  useEffect(() => {
    if (!clienteSelecionado) {
      // Evita manter cliente/usuários globais do cliente anterior
      setResetClienteSelecionado();
      lastFetchedClienteIdRef.current = null;
      return;
    }
    const idCliente = clienteSelecionado.IdCliente;
    if (lastFetchedClienteIdRef.current === idCliente) return;
    lastFetchedClienteIdRef.current = idCliente;
    setClienteSelecionadoRedux({ cliente: clienteSelecionado as any });
    setUsuarios({ idCliente });
  }, [clienteSelecionado, setClienteSelecionadoRedux, setUsuarios, setResetClienteSelecionado]);

  // Atualizar formData quando seletores mudarem
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      IDCLIENTE: clienteSelecionado?.IdCliente || 0,
      IDUSUARIO: usuarioSelecionado?.IdUsuario || undefined
    }));
  }, [clienteSelecionado, usuarioSelecionado]);

  const buscarClientes = async (termoBusca: string) => {
    if (!currentUser) return;

    if (isCliente && clienteSelecionado && termoBusca === '') {
      return;
    }

    setCarregandoClientes(true);

    try {
      let searchIdVendedor = 0;
      let searchIdCliente = 0;

      if (isComercial) {
        searchIdVendedor = currentUser.IdUsuario;
      }

      if (isCliente) {
        searchIdCliente = currentUser.IdCliente;
      }

      const clientes = await buscarClientesService({
        termoBusca,
        IdCliente: searchIdCliente,
        IdUsuario: Number(currentUser.IdUsuario),
        IdVendedor: searchIdVendedor,
        page: 1,
        limit: 50
      });

      if (clientes && clientes.length > 0) {
        setClientesFiltrados(clientes);

        if (isCliente) {
          const clienteDoUsuario = clientes.find((c: any) => Number(c.IdCliente) === Number(currentUser?.IdCliente)) || clientes[0];
          if (clienteDoUsuario) {
            setClienteSelecionado(clienteDoUsuario);
          }
        }
      } else if (!isCliente) {
        setClientesFiltrados([]);
      }
    } catch (error) {
      if (!isCliente) {
        setClientesFiltrados([]);
      }
    } finally {
      setCarregandoClientes(false);
    }
  };

  useEffect(() => {
    if (!currentUser) return;

    const timer = setTimeout(() => {
      if (filtroBusca.length >= 3 || filtroBusca.length === 0) {
        buscarClientes(filtroBusca);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtroBusca, currentUser]);

  // Efeito para buscar "Meus Relatórios" e gerenciar Polling Inteligente
  useEffect(() => {
    const buscarMeusRelatorios = async () => {
      if (!currentUser?.IdUsuario) return;

      dispatch(setCarregandoRelatorios(true));
      try {
        const result = await getMeusRelatorios(90, { silent: true });
        if (result?.success && result?.data?.relatorios) {
          const relatorios = result.data.relatorios;
          // Filtra apenas relatórios do tipo Acesso (IdRelatorioTipo === 1) para armazenar no Redux
          const relatoriosAcesso = relatorios.filter((r: any) => r.IdRelatorioTipo === 1);
          dispatch(setMeusRelatorios(relatoriosAcesso));

          // Se não temos um filaId ativo na tela, mas há algum relatório sendo processado no background, vamos monitorar
          if (filaId === null) {
            const relatorioAtivo = relatoriosAcesso.find((r: any) =>
              r.statusText === 'EM_FILA' || r.statusText === 'EM_ANDAMENTO' || (r.statusText === 'PROCESSADO' && !r.hasFiles)
            );
            if (relatorioAtivo) {
              setFilaId(relatorioAtivo.IdRelatorioFila);
            }
          }
        }
      } catch (error) {
        console.error('Erro ao buscar meus relatórios:', error);
      } finally {
        dispatch(setCarregandoRelatorios(false));
      }
    };

    buscarMeusRelatorios();
  }, [currentUser, getMeusRelatorios, filaId, dispatch]);

  useEffect(() => {
    if (filaId && filaStatus?.statusText === 'PROCESSADO' && filaStatus?.files && filaStatus.files.length > 0) {
      const atualizarRelatorios = async () => {
        try {
          const result = await getMeusRelatorios(90, { silent: true });
          if (result?.success && result?.data?.relatorios) {
            const relatoriosAcesso = result.data.relatorios.filter((r: any) => r.IdRelatorioTipo === 1);
            dispatch(setMeusRelatorios(relatoriosAcesso));
            setTimeout(() => {
              setFilaId(null);
            }, 1000);
          }
        } catch (error) {
          console.error('Erro ao atualizar lista de relatórios após reprocessamento:', error);
        }
      };
      
      atualizarRelatorios();
    }
  }, [filaId, filaStatus, getMeusRelatorios, dispatch]);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!currentUser?.IdUsuario) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      pollingCyclesRef.current = 0;
      return;
    }

    const temRelatoriosProcessandoAtivos = temRelatoriosProcessando(meusRelatorios);
    const temFilaIdAtivo = filaId !== null && filaId !== undefined;
    const temReprocessandoAtivo = reprocessandoIdRef.current !== null;

    // Se não há nada processando e não há fila ativa na busca principal, para o polling
    if (!temRelatoriosProcessandoAtivos && !temFilaIdAtivo && !temReprocessandoAtivo) {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      pollingCyclesRef.current = 0;
      return;
    }

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    if (temRelatoriosProcessandoAtivos) {
      pollingCyclesRef.current = 0;
    }

    pollingIntervalRef.current = setInterval(async () => {
      try {
        const result = await getMeusRelatorios(90, { silent: true });
        if (result?.success && result?.data?.relatorios) {
          const todosRelatorios = result.data.relatorios;
          const relatoriosAcesso = todosRelatorios.filter((r: any) => r.IdRelatorioTipo === 1);

          const relatoriosAtualizados = verificarMudancasRelatorios(relatoriosAcesso, meusRelatorios);
          const deveAtualizar = deveAtualizarListaRelatorios(relatoriosAtualizados, meusRelatorios);
          const filaIdEmProcessamento = filaIdAindaProcessando(filaId, relatoriosAcesso);

          if (deveAtualizar) {
            dispatch(setMeusRelatorios(relatoriosAcesso));
          }

          const filaProcessadaComArquivo = filaId && filaIdProcessadoComArquivo(filaId, relatoriosAcesso);
          if (filaProcessadaComArquivo) {
            setFilaId(null);
          }

          if (reprocessandoIdRef.current !== null) {
            const reprocessadoComArquivo = filaIdProcessadoComArquivo(reprocessandoIdRef.current, relatoriosAcesso);
            const reprocessadoComErro = relatoriosAcesso.find((r: any) => r.IdRelatorioFila === reprocessandoIdRef.current)?.statusText === 'ERRO';
            if (reprocessadoComArquivo || reprocessadoComErro) {
              dispatch(removeReprocessandoId(reprocessandoIdRef.current));
              reprocessandoIdRef.current = null;
              if (!deveAtualizar) dispatch(setMeusRelatorios(relatoriosAcesso));
            }
          }

          const filaAguardandoArquivo = (filaId !== null && !filaIdProcessadoComArquivo(filaId, relatoriosAcesso))
            || (reprocessandoIdRef.current !== null && !filaIdProcessadoComArquivo(reprocessandoIdRef.current, relatoriosAcesso));
          if (!temRelatoriosProcessando(relatoriosAcesso) && !filaIdEmProcessamento && !filaAguardandoArquivo) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            pollingCyclesRef.current = 0;
          }
        }
      } catch (error) {
        console.error('Erro ao atualizar lista de relatórios:', error);
      }
    }, 5000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, [currentUser, getMeusRelatorios, filaId, meusRelatorios, dispatch, temRelatoriosProcessando, verificarMudancasRelatorios, deveAtualizarListaRelatorios, filaIdAindaProcessando, filaIdProcessadoComArquivo]);

  const handleInputChange = (field: keyof IRelatorioAcessRequest, value: any) => {
    setFormData((prev: IRelatorioAcessRequest) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleChangePageMeusRelatorios = (event: unknown, newPage: number) => {
    dispatch(setPageMeusRelatorios(newPage));
  };

  const handleChangeRowsPerPageMeusRelatorios = (event: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setRowsPerPageMeusRelatorios(parseInt(event.target.value, 10)));
    dispatch(setPageMeusRelatorios(0));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setFilaId(null);
    lastSearchReturnedEmptyRef.current = false;
    setIsLoadingData(true);

    try {
      const requestData: IRelatorioAcessRequest = {
        ...formData,
        IDCLIENTE: clienteSelecionado?.IdCliente!,
        RAZAO_SOCIAL_CLIENTE: clienteSelecionado?.RazaoSocial,
        IDUSUARIO: usuarioSelecionado?.IdUsuario,
        IDUSUARIO_VENDEDOR: formData.IDUSUARIO_VENDEDOR,
        IDUSUARIO_REQUISICAO: currentUser?.IdUsuario,
        TIPOCONSUMO: undefined,
        DATAINICIO: formData.DATAINICIO,
        DATAFIM: formData.DATAFIM
      };


      const result = await getRelatorioAcess(requestData);

      // Resetar página do histórico para 0 ao gerar novo relatório para garantir que apareça no topo
      dispatch(setPageMeusRelatorios(0));

      const raw = result?.data;
      const isDirectPayload = raw && typeof raw === 'object' && Array.isArray(raw.data) && (raw.status !== undefined || raw.filaId !== undefined);

      let hasFilaId = false;
      let hasData = false;

      if (isDirectPayload) {
        if (raw.filaId) {
          setFilaId(raw.filaId);
          hasFilaId = true;
        }
        if (raw.data?.length) {
          setDados(raw.data);
          setPage(0);
          hasData = true;
        }
      } else {
        const inner = raw?.data ?? raw;
        if (inner?.filaId) {
          setFilaId(inner.filaId);
          hasFilaId = true;
        }
        const dataArray = Array.isArray(inner?.data) ? inner.data : (Array.isArray(inner) ? inner : []);
        if (dataArray.length > 0) {
          setDados(dataArray);
          setPage(0);
          hasData = true;
        }
      }

      // Se entrou na fila, atualizar lista de "Meus Relatórios" imediatamente
      if (hasFilaId) {
        try {
          const relatoriosResult = await getMeusRelatorios(90, { silent: true });
          if (relatoriosResult?.success && relatoriosResult?.data?.relatorios) {
            const relatoriosAcesso = relatoriosResult.data.relatorios.filter((r: any) => r.IdRelatorioTipo === 1);
            dispatch(setMeusRelatorios(relatoriosAcesso));
          }
        } catch (fError) {
          console.warn('Erro ao atualizar meus relatorios pos-submit:', fError);
        }
      }

      // Só marca como vazio se não houver dados e NÃO for um processo de fila
      lastSearchReturnedEmptyRef.current = !hasData && !hasFilaId;

    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      lastSearchReturnedEmptyRef.current = true;
    } finally {
      setIsLoadingData(false);
    }
  };

  // Se for COMERCIAL, não renderizar nada (o menu já está oculto no ListItems.tsx)
  if (isComercial) {
    return null;
  }

  // Filtra os relatórios do tipo Acesso para exibição
  const relatoriosAcessoParaExibir = meusRelatorios.filter((rel: any) => rel.IdRelatorioTipo === 1);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          Relatório de Acessos
        </Typography>

        {/* Informativo sobre disponibilidade de dados e permissões */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {getMensagemDisponibilidadeRelatorios()}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Campos obrigatórios:</strong> Cliente, Data Início e Data Fim são obrigatórios para gerar o relatório.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Permissões:</strong>{' '}
            {(isDemais || isTecnologiaDaInformacao) && 'Você pode gerar relatórios de todos os clientes.'}
            {isComercial && 'Você pode gerar relatórios apenas dos clientes da sua carteira.'}
            {(isSupervisorComercial || isCX) && 'Você pode gerar relatórios dos clientes comerciais (exceto vendedor Veles).'}
            {isCliente && 'Você pode gerar relatórios apenas da sua empresa.'}
          </Typography>
        </Alert>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Filtros
            </Typography>

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                {/* Seletor de Cliente */}
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={(clientesFiltrados || []).filter((cliente: any) => cliente && cliente.IdCliente)}
                    getOptionLabel={(option) => `${option?.RazaoSocial || ''} - ${option?.NomeFantasia || option?.LoginCliente || ''}`}
                    value={clienteSelecionado}
                    onChange={(event, newValue) => {
                      // Sempre limpar state global ao trocar/limpar cliente
                      setResetClienteSelecionado();
                      setClienteSelecionado(newValue as any);
                      setUsuarioSelecionado(null); // Limpar usuário quando cliente mudar
                    }}
                    onInputChange={(_, newInputValue, reason) => {
                      if (reason === 'input' || reason === 'clear') {
                        setFiltroBusca(newInputValue);
                      }
                    }}
                    loading={carregandoClientes}
                    isOptionEqualToValue={(option: any, value: any) => option?.IdCliente === value?.IdCliente}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={
                          carregandoClientes
                            ? `Buscando clientes...`
                            : `Selecione um Cliente * ${clientesFiltrados.length ? `(${formatarNumero(clientesFiltrados.length)} encontrado${clientesFiltrados.length > 1 ? 's' : ''})` : ''}`
                        }
                        required
                        disabled={isLoadingData}
                        placeholder="Digite para buscar por CNPJ, Razão Social, Login ou Nome Fantasia"
                        helperText="Digite pelo menos 3 caracteres para buscar clientes"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {carregandoClientes ? (
                                <Typography variant="caption" color="primary">
                                  Buscando...
                                </Typography>
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    noOptionsText={
                      carregandoClientes
                        ? "Buscando clientes..."
                        : filtroBusca.length < 3 && filtroBusca.length > 0
                          ? "Digite pelo menos 3 caracteres para buscar"
                          : filtroBusca
                            ? `Nenhum cliente encontrado para "${filtroBusca}"`
                            : "Digite para buscar clientes"
                    }
                    filterOptions={(options) => options} // Desabilitar filtro padrão do MUI
                    freeSolo={false}
                    selectOnFocus={false}
                    clearOnBlur={false}
                  />
                </Grid>

                {/* Seletor de Usuário */}
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={(usuarios || []).filter((usuario: any) => usuario && usuario.IdUsuario)}
                    getOptionLabel={(option: any) => `${option?.Nome || ''} - ${option?.LoginUsuario || ''}`}
                    value={usuarioSelecionado}
                    onChange={(event, newValue) => {
                      setUsuarioSelecionado(newValue as any);
                    }}
                    disabled={isLoadingData}
                    isOptionEqualToValue={(option: any, value: any) => option?.IdUsuario === value?.IdUsuario}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Usuário (Opcional)"
                        placeholder="Selecione um usuário"
                      />
                    )}
                  />
                </Grid>

                {/* Seletor de Produto */}
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    fullWidth
                    options={obterProdutos()}
                    getOptionLabel={(option: any) => {
                      if (option?.IDPRODUTO === 0) {
                        return 'Todos os Produtos';
                      }
                      return option?.NomeProduto || '';
                    }}
                    value={produtoSelecionado}
                    onChange={(event, newValue) => {
                      setProdutoSelecionado(newValue as any);
                    }}
                    disabled={isLoadingData}
                    isOptionEqualToValue={(option: any, value: any) => option?.IDPRODUTO === value?.IDPRODUTO}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Produto (Opcional)"
                        placeholder="Selecione um produto"
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Box>
                          <Typography variant="body2" fontWeight={option?.IDPRODUTO === 0 ? "bold" : "normal"}>
                            {option?.IDPRODUTO === 0 ? 'Todos os Produtos' : (option?.NomeProduto || 'Produto sem nome')}
                          </Typography>
                          {option?.IDPRODUTO !== 0 && option?.Descricao && (
                            <Typography variant="caption" color="text.secondary">
                              {option.Descricao}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}
                  />
                </Grid>


                {/* Data Início */}
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Data Início *"
                    value={formData.DATAINICIO ? new Date(formData.DATAINICIO) : null}
                    onChange={(date) => {
                      // Verificar se a data é válida antes de converter
                      const isValid = date instanceof Date && !isNaN(date.getTime());
                      handleInputChange('DATAINICIO', isValid ? date.toISOString() : undefined);
                    }}
                    onError={(error) => {
                      if (error === 'minDate') {
                        setDataInicioError('Data não pode ser anterior a 3 meses atrás');
                      } else if (error === 'maxDate') {
                        setDataInicioError('Data não pode ser no futuro');
                      } else if (error === 'invalidDate') {
                        setDataInicioError('Data inválida');
                      } else {
                        setDataInicioError(null);
                      }
                    }}
                    minDate={(() => {
                      const dataLimite = new Date();
                      dataLimite.setMonth(dataLimite.getMonth() - 3);
                      dataLimite.setDate(1);
                      return dataLimite;
                    })()}
                    maxDate={new Date()}
                    disabled={isLoadingData}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!dataInicioError,
                        helperText: dataInicioError
                      }
                    }}
                  />
                </Grid>

                {/* Data Fim */}
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Data Fim *"
                    value={formData.DATAFIM ? new Date(formData.DATAFIM) : null}
                    onChange={(date) => {
                      // Verificar se a data é válida antes de converter
                      const isValid = date instanceof Date && !isNaN(date.getTime());
                      handleInputChange('DATAFIM', isValid ? date.toISOString() : undefined);
                    }}
                    onError={(error) => {
                      if (error === 'minDate') {
                        setDataFimError('Data não pode ser anterior a 3 meses atrás');
                      } else if (error === 'maxDate') {
                        setDataFimError('Data não pode ser no futuro');
                      } else if (error === 'invalidDate') {
                        setDataFimError('Data inválida');
                      } else {
                        setDataFimError(null);
                      }
                    }}
                    minDate={(() => {
                      const dataLimite = new Date();
                      dataLimite.setMonth(dataLimite.getMonth() - 3);
                      dataLimite.setDate(1);
                      return dataLimite;
                    })()}
                    maxDate={new Date()}
                    disabled={isLoadingData}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        required: true,
                        error: !!dataFimError,
                        helperText: dataFimError
                      }
                    }}
                  />
                </Grid>

                {/* Configurações de Arquivo */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Configurações de Arquivo
                  </Typography>
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData.generateFiles || false}
                        onChange={(e) => handleInputChange('generateFiles', e.target.checked)}
                        disabled={isLoadingData}
                      />
                    }
                    label="Gerar Arquivos"
                  />
                </Grid>

                {formData.generateFiles && (
                  <>
                    <Grid item xs={12} md={3}>
                      <FormControl fullWidth>
                        <InputLabel>Formato</InputLabel>
                        <Select
                          value={formData.format}
                          onChange={(e) => handleInputChange('format', e.target.value)}
                          disabled={isLoadingData}
                        >
                          <MenuItem value="xlsx">Excel</MenuItem>
                          <MenuItem value="csv">CSV</MenuItem>
                          <MenuItem value="txt">TXT</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {/* Botões */}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoadingData || !clienteSelecionado || !formData.DATAINICIO || !formData.DATAFIM || !!dataInicioError || !!dataFimError}
                    startIcon={<Refresh />}
                    sx={{ mr: 2 }}
                  >
                    {isLoadingData ? 'Gerando...' : 'Gerar Relatório'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>

        {/* --- BLOCO: RESULTADO DO RELATÓRIO (PREVIEW) --- */}
        {hasActiveReport && reportSource && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Resultado do Relatório
                </Typography>
                {isEmAndamento && (
                  <Box sx={{ width: '40%', display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LinearProgress variant="determinate" value={animatedProgress} sx={{ flexGrow: 1, height: 8, borderRadius: 5 }} />
                    <Typography variant="body2" color="text.secondary">{Math.round(animatedProgress)}%</Typography>
                  </Box>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {isEmAndamento ? 'Processando dados... Aguarde.' :
                  isProcessado && formData.generateFiles && !hasFilesForDownload ? 'Dados processados. Iniciando geração do arquivo...' :
                    reportSource.message}
              </Typography>

              {reportSource.statusText === 'ERRO' && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {reportSource.Erro || reportSource.message || 'Erro no processamento'}
                </Alert>
              )}

              {/* Tabela de Preview: Exibe sempre que houver dados, independente de estar processando arquivo ou não */}
              {dadosExibicao.length > 0 && (
                <>
                  {isProcessado && reportSource.message && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {reportSource.message}
                    </Alert>
                  )}

                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {COLUNAS_RELATORIO_ACESSO.map((key) => (
                            <TableCell key={key} sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                              {key}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dadosExibicao
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((row: any, index: number) => (
                            <TableRow key={index} hover>
                              {COLUNAS_RELATORIO_ACESSO.map((key) => {
                                const value = row[key];
                                return (
                                  <TableCell key={key}>
                                    {value}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell colSpan={3} sx={{ fontWeight: 'bold' }}>TOTAL:</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>
                            {formatarNumero(dados.reduce((acc, row) => acc + (Number(row.Quantidade) || 0), 0))}
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <TablePagination
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      component="div"
                      count={dadosExibicao.length}
                      rowsPerPage={rowsPerPage}
                      page={page}
                      onPageChange={handleChangePage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      labelRowsPerPage="Registros por página:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                  </Box>
                </>
              )}

              {isProcessado && dadosExibicao.length === 0 && !hasFilesForDownload && !isLoadingData && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Nenhum dado detalhado para as condições selecionadas.
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {/* Alert só quando o backend respondeu e retornou vazio (não ao apenas selecionar filtros) */}
        {!isLoadingData && !filaId && !filaStatus && lastSearchReturnedEmptyRef.current && dados.length === 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Nenhum registro encontrado</strong> para os filtros selecionados.
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Tente ajustar os filtros ou o período de datas.
            </Typography>
          </Alert>
        )}

        {relatoriosAcessoParaExibir.length > 0 && (
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Meus Relatórios de Acesso
              </Typography>

              {carregandoRelatorios ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <Typography>Carregando relatórios...</Typography>
                  <LinearProgress sx={{ mt: 2 }} />
                </Box>
              ) : (
                <>
                  <TableContainer component={Paper} sx={{ mt: 2 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 'bold', width: 50 }}>Detalhes</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Nome Extração</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Data de Criação</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Download</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {relatoriosAcessoParaExibir
                          .slice(pageMeusRelatorios * rowsPerPageMeusRelatorios, pageMeusRelatorios * rowsPerPageMeusRelatorios + rowsPerPageMeusRelatorios)
                          .map((relatorio: any) => {
                            const isExpanded = relatorioExpandido === relatorio.IdRelatorioFila;
                            const dataCriacao = new Date(relatorio.DataCriacao || relatorio.Inclusao);
                            const dataFormatada = dataCriacao.toLocaleString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric',
                            });

                            const isProcessing = relatorio.statusText === 'EM_FILA' || relatorio.statusText === 'EM_ANDAMENTO' || reprocessandoIds.includes(relatorio.IdRelatorioFila) ||
                              (relatorio.statusText === 'PROCESSADO' && relatorio.IdRelatorioFila === filaId && !(relatorio.hasFiles && relatorio.files?.length) && !(filaStatus?.files && filaStatus.files.length > 0));

                            const hasFiles = (relatorio.hasFiles && relatorio.files && relatorio.files.length > 0) ||
                              (relatorio.IdRelatorioFila === filaId && filaStatus?.files && filaStatus.files.length > 0);

                            return (
                              <React.Fragment key={relatorio.IdRelatorioFila}>
                                <TableRow hover selected={isExpanded}>
                                  <TableCell>
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        dispatch(setRelatorioExpandido(isExpanded ? null : relatorio.IdRelatorioFila));
                                      }}
                                      sx={{ minWidth: 'auto', p: 0.5 }}
                                    >
                                      {isExpanded ? <ExpandLess /> : <ExpandMore />}
                                    </Button>
                                  </TableCell>
                                  <TableCell>{relatorio.NomeExtracao || `Relatório ${relatorio.IdRelatorioFila}`}</TableCell>
                                  <TableCell>{relatorio.Cliente || '-'}</TableCell>
                                  <TableCell>{dataFormatada}</TableCell>
                                  <TableCell>
                                    {isProcessing ? (
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <LinearProgress sx={{ width: 100, height: 6, borderRadius: 1 }} />
                                        <Typography variant="caption" color="text.secondary">
                                          Processando...
                                        </Typography>
                                      </Box>
                                    ) : relatorio.statusText === 'PROCESSADO' && hasFiles ? (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<Download />}
                                        onClick={async () => {
                                          try {
                                            const arquivoParaDownload = (relatorio.files && relatorio.files[0]) ||
                                              (relatorio.IdRelatorioFila === filaId && filaStatus?.files?.[0]);
                                            if (!arquivoParaDownload) return;
                                            await downloadArquivoRelatorio(
                                              arquivoParaDownload,
                                              (relatorios) => {
                                                const relatoriosAcesso = relatorios.filter((r: any) => r.IdRelatorioTipo === 1);
                                                dispatch(setMeusRelatorios(relatoriosAcesso));
                                              },
                                              () => {
                                                if (relatorio.IdRelatorioFila === filaId) {
                                                  setFilaId(null);
                                                }
                                                dispatch(setRelatorioSemArquivo(relatorio.IdRelatorioFila));
                                              }
                                            );
                                          } catch (error: any) {
                                            try {
                                              const result = await getMeusRelatorios(90, { silent: true });
                                              if (result?.success && result?.data?.relatorios) {
                                                const relatoriosAcesso = result.data.relatorios.filter((r: any) => r.IdRelatorioTipo === 1);
                                                dispatch(setMeusRelatorios(relatoriosAcesso));
                                              }
                                            } catch (updateError) {
                                              console.error('Erro ao atualizar lista após falha no download:', updateError);
                                            }
                                          }
                                        }}
                                        sx={{ color: '#9c27b0' }}
                                      >
                                        Baixar
                                      </Button>
                                    ) : (
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color={relatorio.statusText === 'ERRO' ? 'error' : 'primary'}
                                        startIcon={<Replay />}
                                        onClick={async () => {
                                          dispatch(addReprocessandoId(relatorio.IdRelatorioFila));
                                          setFilaId(null);
                                          try {
                                            pollingCyclesRef.current = 0;
                                            await reprocessarRelatorioComPolling(
                                              relatorio.IdRelatorioFila,
                                              (relatorios) => {
                                                const relatoriosAcesso = relatorios.filter((r: any) => r.IdRelatorioTipo === 1);
                                                const atualizado = filaId !== null && filaStatus?.files?.length
                                                  ? relatoriosAcesso.map((r: any) =>
                                                    r.IdRelatorioFila === filaId
                                                      ? { ...r, hasFiles: true, files: filaStatus.files }
                                                      : r
                                                  )
                                                  : relatoriosAcesso;
                                                dispatch(setMeusRelatorios(atualizado));
                                              },
                                              (novaFilaId) => {
                                                setFilaId(novaFilaId);
                                              }
                                            );
                                          } catch (error) {
                                            dispatch(removeReprocessandoId(relatorio.IdRelatorioFila));
                                            console.error('Erro ao reprocessar relatório:', error);
                                          }
                                        }}
                                      >
                                        Reprocessar
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                                {isExpanded && (
                                  <TableRow>
                                    <TableCell colSpan={5} sx={{ py: 2, backgroundColor: 'grey.50' }}>
                                      <Box sx={{ pl: 2 }}>
                                        <Typography variant="subtitle2" gutterBottom>
                                          Detalhes do Relatório
                                        </Typography>
                                        <Grid container spacing={2}>
                                          <Grid item xs={12} md={6}>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Tipo de Arquivo:</strong> {
                                                relatorio.files && relatorio.files.length > 0
                                                  ? Array.from(new Set(relatorio.files.map((file: any) => {
                                                    const tipo = file.type?.toLowerCase() || '';
                                                    if (tipo === FileType.XLSX || file.filename?.endsWith('.xlsx')) return 'Excel';
                                                    if (tipo === FileType.CSV || file.filename?.endsWith('.csv')) return 'CSV';
                                                    if (tipo === FileType.TXT || file.filename?.endsWith('.txt')) return 'TXT';
                                                    if (tipo === FileType.ZIP || file.filename?.endsWith('.zip')) return 'ZIP';
                                                    return 'Desconhecido';
                                                  }))).join(', ')
                                                  : (() => {
                                                      const fmt = relatorio.format?.toLowerCase();
                                                      if (fmt === FileType.XLSX) return 'Excel';
                                                      if (fmt === FileType.CSV) return 'CSV';
                                                      if (fmt === FileType.TXT) return 'TXT';
                                                      return 'N/A';
                                                    })()
                                              }
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Tipo de Relatório:</strong> Acesso
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                              <strong>Status:</strong> {
                                                relatorio.statusText === 'EM_FILA' ? 'Em Fila' :
                                                  relatorio.statusText === 'EM_ANDAMENTO' ? 'Em Andamento' :
                                                    relatorio.statusText === 'PROCESSADO' ? 'Processado' :
                                                      relatorio.statusText === 'ERRO' ? 'Erro' :
                                                        relatorio.statusText || 'Desconhecido'
                                              }
                                            </Typography>
                                          </Grid>
                                          <Grid item xs={12} md={6}>
                                            {relatorio.Erro && (
                                              <Alert severity="error" sx={{ mt: 1 }}>
                                                <Typography variant="body2">
                                                  <strong>Erro:</strong> {relatorio.Erro}
                                                </Typography>
                                              </Alert>
                                            )}
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </React.Fragment>
                            );
                          })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <TablePagination
                      rowsPerPageOptions={[5, 10, 25, 50]}
                      component="div"
                      count={relatoriosAcessoParaExibir.length}
                      rowsPerPage={rowsPerPageMeusRelatorios}
                      page={pageMeusRelatorios}
                      onPageChange={handleChangePageMeusRelatorios}
                      onRowsPerPageChange={handleChangeRowsPerPageMeusRelatorios}
                      labelRowsPerPage="Registros por página:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default RelatoriosAcessos;